// The single "is there a score to show?" predicate for NFL matchup surfaces.
//
// WHY THIS FILE EXISTS. The matchup header carried this test in four places:
//
//     {awayScore > 0 || isFinal ? awayScore.toFixed(1) : '--'}
//
// That is a MAGNITUDE test doing a PRESENCE test's job. A live matchup rendered
// '--' whenever its total was zero or negative -- and in Trash scoring a negative
// total is not an edge case, it is the game. On 2026-09-13 during the noon slate a
// card showed '-- --' for both teams while the player rows beneath it updated
// correctly, because both totals were at or below zero. Stored scores were
// populated and fresh throughout; only the header lied.
//
// The guard was presumably there to avoid printing a score before one exists.
// That intent is preserved exactly -- null and undefined still render '--' -- but
// it is now expressed as the question actually being asked.
//
// ONE definition, imported by both NFLMatchupsPage and NFLMatchupDetail, for the
// same reason the lineup lock is being consolidated (SAB-1164): four copies of a
// predicate is four places to fix it and three places to forget.

/** Rendered when there is genuinely no score yet. */
export const NO_SCORE = '--';

/**
 * Format a score for display.
 *
 * @param val     the score. null/undefined means "no score yet" -> NO_SCORE.
 * @param signed  prefix non-negative values with '+'. TRUE for per-player deltas,
 *                where the sign carries meaning against a baseline of zero. FALSE
 *                for team totals, which the header has always rendered unsigned --
 *                this function must not quietly restyle them.
 *
 * Zero and negative values are REAL SCORES and always render. That is the whole
 * point of the change; see the header note.
 */
export function formatScore(val: number | null | undefined, signed = false): string {
  if (val === null || val === undefined || Number.isNaN(val)) return NO_SCORE;
  return signed && val >= 0 ? `+${val.toFixed(1)}` : val.toFixed(1);
}
